import { api } from './api';
import type {
  Audit,
  AuditConfig,
  AuditType,
  AxisMatrixResponse,
  BrandAxisAuditType,
  EngineInfluenceBlock,
  TriageLog,
  WebsiteAuditSummary,
} from '@traaker/shared';

export const auditsApi = {
  list: (type: AuditType | undefined, brandId: string | undefined): Promise<Audit[]> => {
    // brandId is required server-side. We short-circuit to an empty array
    // when the caller hasn't resolved its `activeBrand` yet — this keeps
    // pages from briefly rendering audits from other brands during the
    // BrandContext resolution race on first mount.
    if (!brandId) return Promise.resolve([]);
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    params.set('brandId', brandId);
    return api.get<Audit[]>(`/audits?${params.toString()}`);
  },

  getById: (auditId: string) =>
    api.get<Audit>(`/audits/${auditId}`),

  getResults: (auditId: string, agent?: string) => {
    const params = agent ? `?agent=${agent}` : '';
    return api.get<any[]>(`/audits/${auditId}/results${params}`);
  },

  getSummary: (auditId: string) =>
    api.get<WebsiteAuditSummary>(`/audits/${auditId}/summary`),

  /**
   * Engine-influence panel ("Influence Moteurs IA"). Brand-owned, BP-computed
   * record — independent of any Earned Media run. Returns null until a Brand
   * Perception audit has populated it (UI hides the tab in that case).
   */
  getEngineInfluence: (brandId: string) =>
    api.get<{ block: EngineInfluenceBlock; computedAt: string } | null>(
      `/audits/engine-influence?brandId=${encodeURIComponent(brandId)}`,
    ),

  getTriage: (auditId: string) =>
    api.get<TriageLog | null>(`/audits/${auditId}/triage`),

  getPageDetail: (auditId: string, agent: string, urlHash: string) =>
    api.get<any>(`/audits/${auditId}/results/${agent}/${urlHash}`),

  create: (type: AuditType, config: AuditConfig, brandId?: string) =>
    api.post<Audit>('/audits', { type, config, brandId }),

  getAxisMatrix: (brandId: string, axis: BrandAxisAuditType, limit = 10) =>
    api.get<AxisMatrixResponse>(
      `/audits/matrix?brandId=${encodeURIComponent(brandId)}&axis=${axis}&limit=${limit}`,
    ),

  /** Live completion progress for a brand-perception fan-out audit. */
  getProgress: (auditId: string, axis: BrandAxisAuditType) =>
    api.get<AuditProgress>(`/audits/${auditId}/progress?axis=${axis}`),

  /** Reco ids the user already actioned for a brand (filtered out on render). */
  listDismissedRecommendations: (brandId: string) =>
    api.get<string[]>(`/audits/dismissed-recommendations?brandId=${encodeURIComponent(brandId)}`),

  dismissRecommendation: (brandId: string, recoId: string) =>
    api.post<{ success: boolean }>('/audits/dismissed-recommendations', { brandId, recoId }),
};

export interface AuditProgress {
  state: 'pending' | 'running' | 'done';
  completed: number;
  expected: number;
  pct: number;
  status?: 'complete' | 'partial';
}
